"use client"

import { useCategory } from "@/hook/useCategory"
import { CategoryData } from "@/lib/config/types"
import { Select, Typography } from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useWebsiteDomainStore } from "./WebsiteSelect"

export interface CategorySelectValue {
  categoryId: number
  categoryName: string
  slug?: string
}

export interface CategorySelectProps {
  value?: CategorySelectValue
  onChange: (value: CategorySelectValue | undefined) => void
  label?: string
  placeholder?: string
  pageSize?: number
  requireWebsite?: boolean
  locale?: string
}

export function CategorySelect({
  value,
  onChange,
  label = "Select Category",
  placeholder = "Select Category",
  pageSize = 10,
  requireWebsite = true,
  locale,
}: CategorySelectProps) {
  const { fetchCategories, loading: hookLoading } = useCategory()
  const websiteDomain = useWebsiteDomainStore(state => state.websiteDomain)
  const [allCategories, setAllCategories] = useState<CategoryData[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadCategories = useCallback(
    async (search: string) => {
      try {
        setLoading(true)
        const params: Record<string, string> = {
          page: "1",
          pageSize: String(pageSize),
        }

        if (requireWebsite && websiteDomain) {
          params.domain = websiteDomain
        } else if (!requireWebsite) {
          params.domain = ""
        }

        if (locale) {
          params.locale = locale
        }

        if (search) {
          params.name = search
        }

        const response = await fetchCategories(params, "preview")
        const categories = Array.isArray(response) ? response : (response as any)?.data || []
        setAllCategories(categories)
      } catch (error) {
        console.error("Failed to load categories:", error)
      } finally {
        setLoading(false)
      }
    },
    [pageSize, requireWebsite, websiteDomain, locale],
  )

  useEffect(() => {
    setSearchValue("")
    setAllCategories([])
    if (!requireWebsite || websiteDomain) {
      loadCategories("")
    }
  }, [websiteDomain, requireWebsite, locale, loadCategories])

  const handleSearch = (val: string) => {
    setSearchValue(val)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      loadCategories(val)
    }, 400)
  }

  const options = useMemo(() => {
    const base = allCategories.map((category: CategoryData) => ({
      value: category.id,
      label: category.attributes.name,
    }))
    if (value?.categoryId && !allCategories.some(cat => cat.id === value.categoryId)) {
      base.unshift({ value: value.categoryId, label: value.categoryName })
    }
    return base
  }, [allCategories, value])

  const handleChange = (selectedValue: number | string) => {
    const selectedCategory = allCategories.find(cat => cat.id === selectedValue)
    if (selectedCategory && onChange) {
      onChange({
        categoryId: selectedValue as number,
        categoryName: selectedCategory.attributes.name,
        slug: selectedCategory.attributes.slug,
      })
    } else if (!selectedValue) {
      onChange(undefined)
    }
  }

  const isDisabled = requireWebsite && !websiteDomain

  return (
    <div>
      <Typography.Title level={5}>{label}</Typography.Title>
      {isDisabled && (
        <Typography.Text type="warning" className="block mb-2">
          Vui lòng chọn Website trước
        </Typography.Text>
      )}
      <Select
        showSearch
        value={value?.categoryId ?? undefined}
        onChange={handleChange}
        onSearch={handleSearch}
        searchValue={searchValue}
        options={options}
        className="w-full"
        loading={loading || hookLoading}
        placeholder={isDisabled ? "Chọn Website trước" : placeholder}
        disabled={isDisabled}
        filterOption={false}
        allowClear
        onClear={() => onChange(undefined)}
      />
    </div>
  )
}
